import type { JSX } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as zod from 'zod';
import type {
  ScenarioComparisonColumn,
  ScenarioComparisonProps,
} from './ScenarioComparison';
import {
  ComparisonModeAndKPIsSelect,
  ComparisonScenariosSelect,
  ScenarioComparisonKPITable,
  getScenarioComparisonKPIColumns,
  useComparisonData,
} from './ScenarioComparison';
import useEvent from './hooks/useEvent';
import type { ClientId } from './core/ClientId';
import {
  ScenarioCommitPolicy,
  findByClientId,
  isSuggestionScenario,
  isUserScenario,
} from './Scenario';
import type {
  SuggestionSchedule,
  UserSchedule,
} from '@seaber/common/core/scenario';
import {
  Box,
  Button,
  FormField,
  FormRow,
  Input,
  Modal,
  ModalBody,
  Select,
  Text,
} from './ui';
import { CompareScenarioTimelines } from './timeline/ScenarioMergeTimeline';
import type { Initial } from '../lib/util';
import { assertDefined } from '../lib/util';
import { LATEST_MIGRATION_VERSION } from './scenario/migrations/migrate';
import type {
  ScenarioImprovement,
  getScenarioImprovements,
} from './core/suggestions/getScenarioImprovements';
import {
  getRequiredLocationPairs,
  getScenarioSwapImprovementKey,
} from './core/suggestions/getScenarioImprovements';
import { useDistances } from './distances/hooks';
import { isTCTimeline } from './core/Timelines';
import { persistedAtom } from '../state/utils/persistedAtom';
import { useAtom } from 'jotai';
import { formatCurrency } from '../lib/format';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ScenarioImprovementsParams } from './ScenarioImprovementParams';
import {
  getScenarioImprovementsParamDefaults,
  scenarioImprovementsParamsSchema,
} from './ScenarioImprovementParams';
import { stringToNumber } from '../lib/zod-util';
import { getScenarioImprovementsInWorker } from './core/PlanningAssistantWorkerMaster';
import { useViolationBasis } from '../state/planning/options';
import type { CargoAllocationOptions } from './core/suggestions/Suggestions';
import { useSpecialRequirements } from './hooks/useSpecialRequirements';

type SelectOption<T extends string> = {
  value: T;
  label: string;
  disabled?: boolean;
};

export type ScenarioImprovementsProps = ScenarioComparisonProps & {
  cargoAllocationOptions: CargoAllocationOptions;
  setImprovementScenarios: (scenarios: SuggestionSchedule[]) => void;
  onAcceptImprovement: (
    improvement: ScenarioImprovement,
    toNewScenario: boolean,
  ) => void;
};

const ignoredScenarioSuggestionsAtom = persistedAtom({
  schema: zod.array(zod.string()),
  key: 'scenario_improvements_ignored_suggestions',
  defaultValue: [],
  getOnInit: false,
});

const useScenarioImprovements = (): {
  isGenerating: boolean;
  startGenerating: (
    ...params: Initial<Parameters<typeof getScenarioImprovements>>
  ) => void;
  stopGenerating: () => void;
  currentResults: ScenarioImprovement[] | null;
  ignoreResult: (result: ScenarioImprovement) => void;
  clearResults: () => void;
} => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentResults, setCurrentResults] = useState<
    ScenarioImprovement[] | null
  >(null);

  const [ignoredResults, setIgnoredResults] = useAtom(
    ignoredScenarioSuggestionsAtom,
  );

  const ignoreResult = useCallback(
    (result: ScenarioImprovement) => {
      const ignoreKey = getScenarioSwapImprovementKey(...result.swap);
      setIgnoredResults([...ignoredResults, ignoreKey]);
      setCurrentResults(currentResults?.filter((r) => r !== result) ?? []);
    },
    [currentResults, ignoredResults, setIgnoredResults],
  );

  const clearResults = useCallback(() => {
    setIgnoredResults([]);
    setCurrentResults(null);
  }, [setIgnoredResults]);

  const [currentGenerateParams, setCurrentGenerateParams] = useState<Initial<
    Parameters<typeof getScenarioImprovements>
  > | null>(null);

  /** We'll always request new results automatically when the parameters or ignored results change,
   * instead of explicitly calling a function to generate from the component.
   * A bit more convenient but could've been a bit easier to read when more straightforward. */
  const doGenerate = useCallback(async () => {
    if (!currentGenerateParams) {
      return;
    }
    setIsGenerating(true);
    const [scenario, timelines, config, context, opts] = currentGenerateParams;
    const results = await getScenarioImprovementsInWorker(
      scenario,
      timelines,
      config,
      context,
      {
        ...opts,
        ignoredSwapSuggestions: ignoredResults,
      },
      async (_results) => {},
    );
    setCurrentResults(results);
    setIsGenerating(false);
  }, [currentGenerateParams, ignoredResults]);

  useEffect(() => {
    void doGenerate();
  }, [doGenerate]);

  /** Sets parameters to generate with */
  const startGenerating = useEvent(
    async (
      ...[scenario, timelines, config, context, opts]: Initial<
        Parameters<typeof getScenarioImprovements>
      >
    ) => {
      setCurrentGenerateParams([scenario, timelines, config, context, opts]);
    },
  );

  const stopGenerating = useCallback(() => {
    setCurrentGenerateParams(null);
  }, []);

  return {
    isGenerating,
    startGenerating,
    stopGenerating,
    currentResults,
    ignoreResult,
    clearResults,
  };
};

const savedSettingsAtom = persistedAtom({
  schema: scenarioImprovementsParamsSchema,
  key: 'scenario_improvements_params',
  defaultValue: getScenarioImprovementsParamDefaults(),
  getOnInit: true,
});

export const ScenarioImprovements = ({
  isOpen,
  onClose,
  scenarios: allScenarios,
  setImprovementScenarios,
  vessels: allVessels,
  orderById,
  calculationConfig,
  organizationsById,
  masterData,
  selectedScenarioData,
  committedData,
  getScenarioData,
  onResetAssignmentsToMaster,
  onConfirmEventOverwrite,
  sharedOrderIds,
  onAcceptImprovement,
  cargoAllocationOptions,
  pnlData,
  scheduleContext,
}: ScenarioImprovementsProps): JSX.Element => {
  const vessels = useMemo(
    () => allVessels.filter((v) => !v.externalFleet),
    [allVessels],
  );
  const selectedScenario = selectedScenarioData.scenario;
  const baseScenarioId = isSuggestionScenario(selectedScenario)
    ? selectedScenario.baseScenarioId
    : selectedScenario.clientId;

  const [view, setView] = useState<'kpi' | 'timeline'>('timeline');

  const baseScenario: UserSchedule = useMemo(() => {
    const scenario = findByClientId(allScenarios, baseScenarioId);
    assertDefined(
      scenario,
      'Expected selected scenario to exist for scenario improvements',
    );
    if (isUserScenario(scenario)) {
      return scenario;
    } else {
      const result: UserSchedule = {
        ...scenario,
        id: null,
        migrationVersion: LATEST_MIGRATION_VERSION,
        entryStates: new Map(),
        type: 'user',
      };
      return result;
    }
  }, [allScenarios, baseScenarioId]);

  const shownScenarios = useMemo(
    () =>
      allScenarios.filter(
        (s) =>
          s.clientId === baseScenario.clientId ||
          (isSuggestionScenario(s) &&
            s.baseScenarioId === baseScenario.clientId),
      ),
    [allScenarios, baseScenario.clientId],
  );
  const selectedScenarios = useMemo(
    () => shownScenarios.map((s) => s.clientId),
    [shownScenarios],
  );

  const comparisonScenarios = useMemo(
    () =>
      shownScenarios.filter(
        (s) =>
          s.clientId === baseScenario.clientId ||
          selectedScenarios.includes(s.clientId),
      ),
    [shownScenarios, baseScenario.clientId, selectedScenarios],
  );

  const dateFilter = useMemo(() => ({ start: 0, end: null }), []);

  const filteredVesselIds = useMemo(
    () => new Set(vessels.map((v) => v.id)),
    [vessels],
  );
  const {
    comparisonData,
    loadingScenarios,
    incomeByScenario,
    reletCostsByScenario,
    subTimelinesByScenarioCId,
    calculateSubTimeline,
    timelinesByScenario,
  } = useComparisonData(
    comparisonScenarios,
    selectedScenarios,
    calculationConfig,
    getScenarioData,
    filteredVesselIds,
    dateFilter,
    pnlData,
  );

  const baseTimelines = useMemo(() => {
    const result = timelinesByScenario.get(baseScenario.clientId);
    return result?.filter(isTCTimeline).filter((timeline) => {
      const { vessel } = timeline;
      return vessel && !vessel.externalFleet && !vessel.inactive;
    });
  }, [timelinesByScenario, baseScenario]);

  const columns = useMemo<ScenarioComparisonColumn[]>(
    () =>
      getScenarioComparisonKPIColumns(
        comparisonData,
        orderById,
        comparisonScenarios,
        selectedScenarios,
      ),
    [comparisonData, orderById, comparisonScenarios, selectedScenarios],
  );

  const activeSuggestionScenario = useMemo(
    () =>
      selectedScenarios.length > 1
        ? allScenarios
            .filter(isSuggestionScenario)
            .find(
              (s) =>
                s.clientId === selectedScenarios[selectedScenarios.length - 1],
            )
        : null,
    [allScenarios, selectedScenarios],
  );
  const comparisonBaseData = useMemo(
    () => comparisonData.get(baseScenarioId),
    [baseScenarioId, comparisonData],
  );
  const comparisonCurrent = useMemo(
    () =>
      activeSuggestionScenario &&
      comparisonData.get(activeSuggestionScenario.clientId),
    [activeSuggestionScenario, comparisonData],
  );
  const currentProfitImprovement = useMemo(() => {
    if (
      comparisonBaseData?.totalIncome == null ||
      comparisonCurrent?.totalIncome == null
    )
      return null;
    const baseProfit =
      comparisonBaseData.totalIncome - comparisonBaseData.totalCost;
    const currentProfit =
      comparisonCurrent.totalIncome - comparisonCurrent.totalCost;
    return currentProfit - baseProfit;
  }, [comparisonBaseData, comparisonCurrent]);

  const currentBallastImprovement = useMemo(() => {
    if (
      comparisonBaseData?.totalBallastDistance == null ||
      comparisonCurrent?.totalBallastDistance == null
    )
      return null;
    return (
      comparisonCurrent.totalBallastDistance -
      comparisonBaseData.totalBallastDistance
    );
  }, [comparisonBaseData, comparisonCurrent]);

  const isChangeAnImprovement = useMemo(() => {
    return (
      (currentProfitImprovement !== null && currentProfitImprovement > 0) ||
      (currentBallastImprovement !== null && currentBallastImprovement < 0)
    );
  }, [currentProfitImprovement, currentBallastImprovement]);

  const isScenarioLoading = useCallback(
    (scenarioId: ClientId) =>
      loadingScenarios.has(scenarioId) ||
      !incomeByScenario.has(scenarioId) ||
      !reletCostsByScenario.has(scenarioId),
    [incomeByScenario, loadingScenarios, reletCostsByScenario],
  );

  const {
    isGenerating,
    startGenerating,
    stopGenerating,
    currentResults,
    ignoreResult,
    clearResults,
  } = useScenarioImprovements();

  useEffect(() => {
    setImprovementScenarios(currentResults?.map((r) => r.scenario) ?? []);
  }, [currentResults, setImprovementScenarios]);

  const { fetchMissingDistances } = useDistances();

  const violationBasis = useViolationBasis();

  const specialRequirements = useSpecialRequirements();

  const handleGetImprovementsClick = useEvent(async () => {
    if (!baseTimelines) {
      return;
    }
    const requiredLocationPairs = getRequiredLocationPairs(
      baseTimelines,
      Date.now(),
    );
    const updatedDistances = await fetchMissingDistances(
      scheduleContext.distances,
      new Set(requiredLocationPairs.flatMap(([from, to]) => [from, to])),
      false,
    );

    if (!baseTimelines) {
      return;
    }

    startGenerating(
      baseScenario,
      baseTimelines,
      calculationConfig,
      {
        ...scheduleContext,
        distances: updatedDistances,
      },
      {
        startAt: Date.now(),
        cargoAllocationOptions,
        violationLimitsD: form.getValues('violationLimitsD'),
        cargoExclusions: form.getValues('cargoExclusions'),
        violationBasis,
        specialRequirements,
      },
    );
  });

  const handleAcceptScenario = useEvent(
    (scenario: SuggestionSchedule, toNewScenario: boolean) => {
      stopGenerating();
      clearResults();
      const improvement = currentResults?.find(
        (r) => r.scenario.clientId === scenario.clientId,
      );
      assertDefined(improvement, 'Expected improvement to exist');
      onAcceptImprovement(improvement, toNewScenario);
    },
  );

  const handleIgnoreResult = useEvent(
    (scenario: ScenarioImprovement['scenario']) => {
      const result = currentResults?.find((r) => r.scenario === scenario);
      assertDefined(result, 'Expected result to exist');
      console.log('handleIgnore', { scenario, result });
      ignoreResult(result);
    },
  );

  const [savedParams, setSavedParams] = useAtom(savedSettingsAtom);

  const form = useForm<ScenarioImprovementsParams>({
    defaultValues: savedParams,
    resolver: zodResolver(scenarioImprovementsParamsSchema),
    mode: 'onChange',
  });

  const {
    register,
    formState: { errors },
  } = form;

  useEffect(() => {
    const subscription = form.watch((values) => {
      const parseResult = scenarioImprovementsParamsSchema.safeParse(values);
      if (parseResult.success) {
        setSavedParams(parseResult.data);
        stopGenerating();
        clearResults();
      } else {
        console.error(
          `Failed to parse form data on ScenarioImprovements: ${parseResult.error}`,
        );
      }
    });

    return () => subscription.unsubscribe();
  });

  const commodityExclusionOptions: SelectOption<
    ScenarioImprovementsParams['cargoExclusions']
  >[] = [
    {
      value: 'exclude',
      label: 'Exclude: no suggestions with violations',
    },
    {
      value: 'warn',
      label: 'Warn: allowed, warning shown',
    },
  ];

  return (
    <Modal
      width="100%"
      height="100%"
      isOpen={isOpen}
      onClose={() => {
        setImprovementScenarios([]);
        onClose();
      }}
    >
      <ModalBody p={0} overflow="hidden" width="100%">
        <Box
          p={4}
          bg="neutral.white_100"
          width="100%"
          display="flex"
          flexDirection="row"
        >
          <Box display="flex" flexDirection="column" marginRight={5}>
            <FormRow>
              <ComparisonScenariosSelect
                scenarios={shownScenarios}
                selectedScenarios={selectedScenarios}
                handleScenarioToggleClick={() => null}
              />
            </FormRow>
            <FormRow>
              <ComparisonModeAndKPIsSelect view={view} setView={setView} />
            </FormRow>
          </Box>

          <Box
            width="1px"
            height="100%"
            bg="neutral.gray_150"
            my={1}
            marginRight={5}
          />

          <Box
            display="flex"
            flexDirection="column"
            alignItems="start"
            marginRight={8}
          >
            <FormRow>
              <FormField
                label="Max load early (h)"
                width={170}
                hasError={!!errors.violationLimitsD?.load?.early}
              >
                <Input
                  type="number"
                  style={{ width: '5em' }}
                  step={0.1}
                  min={0}
                  placeholder="∞"
                  id="violationLimitsD.load.early"
                  {...register('violationLimitsD.load.early', {
                    setValueAs: stringToNumber,
                  })}
                  data-testid={`violationLimitsD.load.early`}
                />
              </FormField>
              <FormField
                label="Max load late (h)"
                width={170}
                hasError={!!errors.violationLimitsD?.load?.late}
              >
                <Input
                  type="number"
                  style={{ width: '5em' }}
                  step={0.1}
                  min={0}
                  placeholder="∞"
                  id="violationLimitsD.load.late"
                  {...register('violationLimitsD.load.late', {
                    setValueAs: stringToNumber,
                  })}
                  data-testid={`violationLimitsD.load.late`}
                />
              </FormField>
            </FormRow>
            <FormRow>
              <FormField
                label="Max discharge early (h)"
                width={170}
                hasError={!!errors.violationLimitsD?.discharge?.early}
              >
                <Input
                  type="number"
                  style={{ width: '5em' }}
                  step={0.1}
                  min={0}
                  placeholder="∞"
                  id="violationLimitsD.discharge.early"
                  {...register('violationLimitsD.discharge.early', {
                    setValueAs: stringToNumber,
                  })}
                  data-testid={`violationLimitsD.discharge.early`}
                />
              </FormField>
              <FormField
                label="Max discharge late (h)"
                width={170}
                hasError={!!errors.violationLimitsD?.discharge?.late}
              >
                <Input
                  type="number"
                  style={{ width: '5em' }}
                  step={0.1}
                  min={0}
                  placeholder="∞"
                  id="violationLimitsD.discharge.late"
                  {...register('violationLimitsD.discharge.late', {
                    setValueAs: stringToNumber,
                  })}
                  data-testid={`violationLimitsD.discharge.late`}
                />
              </FormField>
            </FormRow>
            <FormRow>
              <FormField
                label="Commodity exclusions"
                width={320}
                hasError={!!errors.cargoExclusions}
              >
                <Select
                  {...register('cargoExclusions')}
                  options={commodityExclusionOptions}
                  required
                />
              </FormField>
            </FormRow>
          </Box>

          <Box
            width="1px"
            height="100%"
            bg="neutral.gray_150"
            my={1}
            marginRight={5}
          />

          <Box
            display="flex"
            flexDirection="column"
            alignItems="start"
            marginRight={8}
          >
            <Box display="flex" flexDirection="column">
              <Text fontSize={2} fontWeight="bold" marginBottom={2}>
                Suggestion
              </Text>
              {activeSuggestionScenario ? (
                <Box display="flex" flexDirection="row">
                  <Button
                    variant="primary"
                    marginRight={2}
                    onPress={() =>
                      handleAcceptScenario(activeSuggestionScenario, false)
                    }
                  >
                    Accept
                  </Button>
                  <Button
                    variant="primary"
                    marginRight={2}
                    onPress={() =>
                      handleAcceptScenario(activeSuggestionScenario, true)
                    }
                  >
                    Accept to new scenario
                  </Button>
                  <Button
                    variant="default"
                    onPress={() => handleIgnoreResult(activeSuggestionScenario)}
                  >
                    Ignore
                  </Button>
                </Box>
              ) : (
                <Button
                  disabled={
                    isScenarioLoading(selectedScenarios[0]) || isGenerating
                  }
                  onPress={handleGetImprovementsClick}
                >
                  {isGenerating && 'Generating improvements...'}
                  {!isGenerating && 'Get improvements'}
                </Button>
              )}
              {!isGenerating && currentResults?.length === 0 && (
                <Box display="flex" flexDirection="column">
                  <Text fontSize={2} fontWeight="bold" marginTop={3}>
                    No improvements found
                  </Text>
                </Box>
              )}
              {comparisonCurrent && (
                <Box display="flex" flexDirection="column">
                  <Text fontSize={2} fontWeight="bold" marginTop={3}>
                    Impact
                  </Text>
                  {!isChangeAnImprovement && (
                    <Text fontSize={2} marginTop={2}>
                      {
                        "Couldn't find improvements with the query parameters, showing next best alternatives instead."
                      }
                    </Text>
                  )}
                  {currentProfitImprovement !== null && (
                    <Text
                      fontSize={2}
                      marginTop={2}
                      color={
                        currentProfitImprovement < 0
                          ? 'red'
                          : currentProfitImprovement > 0
                            ? 'green'
                            : undefined
                      }
                    >
                      Profit:{' '}
                      <b>
                        {currentProfitImprovement > 0 ? '+' : ''}
                        {formatCurrency(
                          currentProfitImprovement,
                          0,
                          calculationConfig.defaultCurrency,
                        )}
                      </b>
                    </Text>
                  )}
                  {currentBallastImprovement !== null && (
                    <Text fontSize={2} marginTop={2}>
                      Ballast distance:{' '}
                      <b>
                        {currentBallastImprovement > 0 ? '+' : ''}
                        {currentBallastImprovement.toFixed(0)} NM
                      </b>
                    </Text>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        <Box width="100%" height="100%" overflow="auto">
          {view === 'kpi' ? (
            <ScenarioComparisonKPITable
              columns={columns}
              isScenarioLoading={isScenarioLoading}
            />
          ) : (
            <CompareScenarioTimelines
              scheduleContext={scheduleContext}
              organizations={organizationsById}
              masterData={masterData}
              selectedScenarioData={selectedScenarioData}
              selectedScenarios={selectedScenarios}
              timelinesByScenario={subTimelinesByScenarioCId}
              calculateSubTimeline={calculateSubTimeline}
              scenarios={comparisonScenarios}
              baseScenarioId={baseScenario.clientId}
              committedData={committedData}
              onResetAssignmentsToMaster={onResetAssignmentsToMaster}
              onConfirmEventOverwrite={onConfirmEventOverwrite}
              sharedOrderIds={sharedOrderIds}
              getScenarioData={getScenarioData}
              commitPolicy={ScenarioCommitPolicy.OVERRIDE}
              includeResult={false}
            />
          )}
        </Box>
      </ModalBody>
    </Modal>
  );
};

export default ScenarioImprovements;
